import { useQuery, useQueryClient } from "@tanstack/react-query";
import { message } from "antd";
import { useApiMutation, useApiQuery } from "../hooks/useApiRequest";
import { fetcher } from "../hooks/fetcher";
import { MY_ACCOUNT_KEY } from "./myAccountService";

export interface UserRecord {
  _id?: string;
  email?: string;
  username?: string;
  payID?: string;
  status?: string;
  personal_info?: {
    fullName?: string;
    firstName?: string;
    lastName?: string;
    name?: string;
    legal_name?: string;
    lastname?: string;
    legal_lastname?: string;
  };
}

export interface UserListResponse {
  results?: UserRecord[];
}

export interface PaginatedUserListResponse {
  results?: UserRecord[];
  totalItems?: number;
  totalPages?: number;
  currentPage?: number;
  limit?: number;
}

interface UserListQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  administrativeRoleId?: string;
  scope?: string;
}

export interface SecretResponse {
  otpauth_url?: string;
  base32?: string;
}

export interface UserBalanceRecord {
  _id?: string;
  asset?: string;
  assetName?: string;
  assetImage?: string;
  total?: string;
  locked?: string;
  availableAmount?: string;
  availableAmountFormatted?: string;
  available?: {
    $numberDecimal?: string;
  };
  accountName?: string;
  accountId?: string;
}

/**
 * 🔴 NO `ipAddress`. The API reads the IP off the connection (`req.ip`,
 * resolved against its trust-proxy list) and ignores the body entirely — a
 * browser cannot learn its own public IP without a third-party call the
 * console's CSP blocks, and a client-supplied IP in a security-audit record is
 * a claim rather than a fact.
 */
export interface LoginHistoryPayload {
  device: string;
  browser: string;
}

interface QueryValues {
  [key: string]: unknown;
}

interface ErrorWithMessage {
  message?: string;
}

const getErrorMessage = (error: unknown, fallback: string) => {
  if (error instanceof Error) {
    return error.message || fallback;
  }

  if (
    typeof error === "object" &&
    error !== null &&
    "message" in error &&
    typeof (error as ErrorWithMessage).message === "string"
  ) {
    return (error as ErrorWithMessage).message || fallback;
  }

  return fallback;
};

//* ---------Get all users---------
export const useGetAllUsers = () => {
  return useApiQuery<UserListResponse>({
    key: ["clients"],
    endPoint: `/api/v2/admin/users`,
    method: "GET",
  });
};

export const useGetAdminUsersPageService = ({
  page = 1,
  limit = 10,
  search,
  administrativeRoleId,
  scope,
}: UserListQueryParams = {}) => {
  const normalizedSearch = search?.trim();
  const normalizedAdministrativeRoleId = administrativeRoleId?.trim();
  const normalizedScope = scope?.trim();
  const searchSuffix = normalizedSearch
    ? `&search=${encodeURIComponent(normalizedSearch)}`
    : "";
  const administrativeRoleSuffix = normalizedAdministrativeRoleId
    ? `&administrativeRoleId=${encodeURIComponent(normalizedAdministrativeRoleId)}`
    : "";
  const scopeSuffix = normalizedScope
    ? `&scope=${encodeURIComponent(normalizedScope)}`
    : "";

  return useApiQuery<PaginatedUserListResponse>({
    key: [
      "clients-index",
      page,
      limit,
      normalizedSearch,
      normalizedAdministrativeRoleId,
      normalizedScope,
    ],
    endPoint: `/api/v2/admin/users?page=${page}&limit=${limit}${searchSuffix}${administrativeRoleSuffix}${scopeSuffix}`,
    method: "GET",
  });
};

export const useGetUserAccounts = (userId?: string) => {
  return useApiQuery({
    key: ["chains"],
    endPoint: `/api/v2/sub-accounts?userId=${userId}`,
    method: "GET",
  });
};

//* ---------Get user balances (aggregated across the client's sub-accounts)---------
// The aggregate `/clients/:id/balances` route is newer and isn't live on every
// deployed API yet (it 404s where it hasn't shipped). The per-account route
// `/clients/:id/sub-accounts/:accountId/balances` is long-standing — it's what the
// legacy admin reads — so fan out across the client's sub-accounts and merge by asset.
// This works regardless of deploy state and is the proven path.
const toBalanceNumber = (value: unknown): number => {
  if (value == null) return 0;
  if (typeof value === "number") return value;
  if (
    typeof value === "object" &&
    value !== null &&
    "$numberDecimal" in value
  ) {
    return (
      Number((value as { $numberDecimal?: string }).$numberDecimal ?? 0) || 0
    );
  }
  return Number(value) || 0;
};

export interface PortfolioEstimateHolding {
  asset: string;
  assetName?: string;
  assetImage?: string;
  total: number;
  available: number;
  locked: number;
  /** ZAR value at current prices — null = price unavailable (unknown, never 0). */
  valueZar: number | null;
  /** Percentage of the priced total — null when this holding is unpriced. */
  share: number | null;
  priceUnavailable: boolean;
}

/**
 * GET /admin/clients/{id}/portfolio-estimate — every asset across every
 * sub-account valued in ZAR at CURRENT prices (the same price source the
 * customer's own app uses). `partial` means one or more holdings could not be
 * priced and the total reads as a floor; `totalZar` null means the whole book
 * is unpriced (unknown, not R0).
 */
export interface ClientPortfolioEstimate {
  totalZar: number | null;
  totalUsdt: number | null;
  usdRateUnavailable: boolean;
  partial: boolean;
  unpricedAssets: string[];
  holdings: PortfolioEstimateHolding[];
  pricedAt: string;
}

export const useGetClientPortfolioEstimate = (userId?: string) => {
  return useQuery<ClientPortfolioEstimate | null, Error>({
    queryKey: ["client-portfolio-estimate", userId],
    enabled: !!userId,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    queryFn: async () => {
      // fetcher already unwraps the {success, data} envelope — the result IS
      // the estimate object. A second .data unwrap here would always be
      // undefined and silently blank the tile.
      const res = await fetcher<ClientPortfolioEstimate>(
        `/api/v2/admin/clients/${userId}/portfolio-estimate`,
        "GET"
      );
      return res ?? null;
    },
  });
};

interface SubAccountListResult {
  data?: Array<{ _id?: string }>;
}
interface AccountBalanceRow {
  asset?: string;
  total?: unknown;
  available?: unknown;
  locked?: unknown;
}

export const useGetUserBalances = (userId?: string) => {
  return useQuery<UserBalanceRecord[], Error>({
    queryKey: ["user-balances", userId],
    enabled: !!userId,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    queryFn: async () => {
      const accountsRes = await fetcher<SubAccountListResult>(
        `/api/v2/admin/clients/${userId}/sub-accounts?page=1&limit=100`,
        "GET"
      );
      const accountIds = (
        Array.isArray(accountsRes?.data) ? accountsRes.data : []
      )
        .map((account) => account?._id)
        .filter((id): id is string => typeof id === "string" && id.length > 0);
      if (accountIds.length === 0) return [];

      const perAccount = await Promise.all(
        accountIds.map((accountId) =>
          fetcher<AccountBalanceRow[]>(
            `/api/v2/admin/clients/${userId}/sub-accounts/${accountId}/balances`,
            "GET"
          ).catch(() => [] as AccountBalanceRow[])
        )
      );

      const byAsset = new Map<
        string,
        { total: number; available: number; locked: number }
      >();
      for (const rows of perAccount) {
        for (const row of Array.isArray(rows) ? rows : []) {
          const symbol = typeof row?.asset === "string" ? row.asset : "—";
          const acc = byAsset.get(symbol) ?? {
            total: 0,
            available: 0,
            locked: 0,
          };
          acc.total += toBalanceNumber(row?.total);
          acc.available += toBalanceNumber(row?.available);
          acc.locked += toBalanceNumber(row?.locked);
          byAsset.set(symbol, acc);
        }
      }

      const fmt = (n: number) => String(Number(n.toFixed(8)));
      return Array.from(byAsset.entries())
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([asset, value]) => ({
          asset,
          total: fmt(value.total),
          locked: fmt(value.locked),
          availableAmount: fmt(value.available),
          availableAmountFormatted: fmt(value.available),
        }));
    },
  });
};

//*********** add Login History **********
export const useAddLoginHistoryService = () => {
  const queryClient = useQueryClient();
  const { mutateAsync, error } = useApiMutation();

  const addLoginHistory = async (
    values: LoginHistoryPayload,
    onSuccess: () => void,
    onError: (error: unknown) => void
  ) => {
    try {
      await mutateAsync({
        key: ["login-history"],
        endPoint: `/api/v2/login-history`,
        method: "POST",
        bodyContent: values,
      });
      queryClient.invalidateQueries({ queryKey: ["login-history"] });
      onSuccess();
    } catch (error) {
      // ONE failure, ONE toast. This used to call `onError()` AND raise its own
      // `message.error`, so a single rejected request put two red toasts in
      // front of the operator — and the second was the server's raw text, which
      // for a schema rejection is the ORM's own wording naming the model and the
      // field. `onError` receives the error so the caller can decide; it is the
      // caller that knows what the operator should be told.
      onError(error);
    }
  };

  return { addLoginHistory, error };
};

//* ---------Get Secret Key---------
export const useGetSecret = () => {
  return useApiQuery<SecretResponse>({
    key: ["secret-key"],
    endPoint: `/api/v2/settings/security/2fa/secret`,
    method: "GET",
  });
};

//*********** Verify Code **********
export const useVerifyCodeService = () => {
  const queryClient = useQueryClient();
  const { mutateAsync, error } = useApiMutation();

  const verifyCode = async (
    values: QueryValues,
    onSuccess: () => void,
    onError: () => void
  ) => {
    try {
      await mutateAsync({
        key: ["verify-codes"],
        endPoint: `/api/v2/settings/security/2fa/verify`,
        method: "PATCH",
        bodyContent: {
          code:
            typeof values.code === "string"
              ? values.code
              : typeof values.token === "string"
                ? values.token
                : "",
        },
      });
      queryClient.invalidateQueries({ queryKey: ["secret-key"] });
      queryClient.invalidateQueries({ queryKey: ["verify-codes"] });
      // ...and the profile, which is what actually renders the enrolled/not
      // status. Without this the card keeps offering "Enable" until a reload.
      queryClient.invalidateQueries({ queryKey: [MY_ACCOUNT_KEY] });
      onSuccess();
      message.success("2FA successfully enabled");
    } catch (error) {
      onError();
      message.error(getErrorMessage(error, "Failed to verify code."));
    }
  };

  return { verifyCode, error };
};

//*********** Disable 2FA **********
/**
 * Turn OFF the signed-in operator's own TOTP.
 *
 * The server demands a current code (`PATCH /settings/security/2fa/disable`
 * → `verifyTotpTokenOnce`): removing the second factor is itself a sensitive
 * action, so proving possession is the point, not a formality.
 *
 * Invalidates the account profile because `twoFactorAuth.enabled` there is what
 * decides whether the card offers Enable or Disable.
 */
export const useDisable2faService = () => {
  const queryClient = useQueryClient();
  const { mutateAsync, error } = useApiMutation();

  const disable2fa = async (
    token: string,
    onSuccess: () => void,
    onError: () => void
  ) => {
    try {
      await mutateAsync({
        key: ["disable-2fa"],
        endPoint: `/api/v2/settings/security/2fa/disable`,
        method: "PATCH",
        bodyContent: { token },
      });
      queryClient.invalidateQueries({ queryKey: ["secret-key"] });
      queryClient.invalidateQueries({ queryKey: [MY_ACCOUNT_KEY] });
      onSuccess();
      message.success("2FA disabled");
    } catch (err) {
      onError();
      message.error(getErrorMessage(err, "Failed to disable 2FA."));
    }
  };

  return { disable2fa, error };
};
